/* prettier-ignore */

import { computed, effect, inject, Injectable, NgZone } from '@angular/core';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { filter, groupBy, GroupedObservable, ignoreElements, map, merge, Observable, tap,
   retry, shareReplay, Subject, Subscription, take, takeUntil, timer, mergeMap,
   concatMap, catchError, EMPTY, delay, of, mapTo, from, timeout as rxTimeout
} from 'rxjs';

import { DebugService } from 'src/app/services/debug.service';
import { RestService } from 'src/app/services/rest.service';
import { environment } from 'src/environments/environment';
import { ChatBotTextAnimationService } from './textAnimationService.service';
import { PlayAudioHandlerService } from './playAudioHandler.service';
import { DataChatService } from './dataChat.service';
import { ChatActionHandlerService } from './chatActionsHandler.service';

const MAX_TRACKED_IDS = 50;

@Injectable({ providedIn: 'root' })
export class WebSocketService {
  private readonly ngZone = inject(NgZone);
  private readonly dataChat = inject(DataChatService);
  private readonly playService = inject(PlayAudioHandlerService);
  private readonly debugService = inject(DebugService);
  private readonly actionService = inject(ChatActionHandlerService);
  private readonly textAnimation = inject(ChatBotTextAnimationService);
  private readonly rest = inject(RestService);

  /**
   * HRS-2579: de qué app (`name_index`) viene la conversación. Un mismo chatbot
   * atiende a las apps de todos los municipios de una comarca, así que sin esto
   * el asistente no sabe qué configuración leer. Aquí se sabe de sobra: es el
   * destino que la web ya tiene resuelto.
   */
  private readonly urlWebSocket = computed(() => {
    const base = `${environment.urlWebSocket}/${this.dataChat.token()}/${this.dataChat.id()}`;
    const app = (this.rest.subDomain || '').trim();

    return app ? `${base}?app=${encodeURIComponent(app)}` : base;
  });
  private readonly urlWebSocketTools = computed(() => `${environment.urlWebSocketTools}/${this.dataChat.token()}`);

  private wsSend!: WebSocketSubject<any>;
  private wsRecv$!: Observable<any>;
  private wsSub?: Subscription;

  private toolsSend!: WebSocketSubject<any>;
  private toolsRecv$!: Observable<any>;
  private toolsSub?: Subscription;

  private readonly cancelled = new Set<string>();
  private cancel$ = new Subject<string>();
  private disconnect$ = new Subject<void>();

  private connected = false;
  private activeCompletionId: string | null = null;
  private readonly completionIds = new Set<string>();

  private feedbackOpenAIEffect = effect(() => {
    if (!this.actionService.openAIFeedback())
      return;

    // HRS-3045 (bloque 4): las acciones del flujo SSE también producen feedback de
    // tools. Si los sockets no están abiertos (tenant solo-texto), se abren aquí
    // bajo demanda; WebSocketSubject bufferiza el next() hasta que el socket abre.
    this.ensureConnected();

    this.debugService.info(this.actionService.openAIFeedback(), "[WebSocketService] Sending Tools Feedback...");
    try {
      this.toolsSend.next(this.actionService.openAIFeedback());
    } catch (err) {
      this.debugService.error(err, '[WS] fallo enviando tools feedback');
    }
    this.actionService.openAIFeedback.set(null);
  }, { allowSignalWrites: true });

  /**
   * HRS-3045 (bloque 4): conexión capability-driven. Los sockets solo se consumen
   * cuando hay voz o imagen activas (las respuestas de ambos flujos llegan como
   * chunks por el socket de asistente y sus acciones por el de tools); el texto va
   * por SSE v3 + polling y no los necesita. Si las capabilities llegan tarde
   * (config del tenant, cookie o postMessage del embed), este efecto conecta en
   * cuanto aparecen; en los resets, el token nuevo re-dispara la evaluación.
   */
  private capabilityConnectEffect = effect(() => {
    const wantsSockets = this.dataChat.allowSendAudio() || this.dataChat.allowSendImage();
    const ready = !!this.dataChat.token() && !!this.dataChat.id();
    if (wantsSockets && ready && !this.connected)
      this.connectAll();
  });

// ──────────────────────── Connection lifecycle ────────────────────────

  /**
   * Conecta solo si alguna capability activa consume los sockets (voz/imagen).
   * Punto de entrada para el arranque del chat y los resets; los flujos que
   * necesitan el socket sí o sí (grabar audio, enviar imagen, feedback de tools)
   * usan ensureConnected().
   */
  connectIfNeeded(): void {
    if (this.dataChat.allowSendAudio() || this.dataChat.allowSendImage()) {
      this.connectAll();
      return;
    }
    this.debugService.info('WS omitido: capabilities activas no lo consumen (texto va por SSE)', '[WS]');
  }

  /** Conexión perezosa: garantiza sockets abiertos justo antes de usarlos. */
  ensureConnected(): void {
    if (!this.connected)
      this.connectAll();
  }

  connectAll(): void {
    if (this.connected) {
      this.debugService.warn('Already active WebSocket connection', '[WS]');
      return;
    }

    this.debugService.info('Connecting WebSocket…', '[WS]');
    const assistant = this.createSocket(this.urlWebSocket());
    this.wsSend = assistant.subject;
    this.wsRecv$ = assistant.recv$;

    const tools = this.createSocket(this.urlWebSocketTools());
    this.toolsSend = tools.subject;
    this.toolsRecv$ = tools.recv$ as Observable<any>;

    this.ngZone.runOutsideAngular(() => {
      this.wsSub = this.buildPipeline()
        .pipe(takeUntil(this.disconnect$))
        .subscribe();

      this.toolsSub = this.setupToolsPipeline()
        .pipe(takeUntil(this.disconnect$))
        .subscribe();
    });

    this.connected = true;
  }

  disconnect(): void {
    this.disconnect$.next();

    this.wsSub?.unsubscribe();
    this.wsSub = undefined;

    this.toolsSub?.unsubscribe();
    this.toolsSub = undefined;

    try { this.wsSend?.complete(); } catch {}
    this.wsSend = undefined!;

    try { this.cancel$.complete(); } catch {}
    this.cancelled.clear();
    this.completionIds.clear();

    this.disconnect$.complete();
    this.cancel$ = new Subject<string>();
    this.disconnect$ = new Subject<void>();
    this.connected = false;
    // this.activeCompletionId = null;

    this.debugService.warn('WebSocket disconnected', '[WS]');
  }

// ──────────────────────── Socket factory ────────────────────────
  private createSocket(url: string): {
    subject: WebSocketSubject<any>;
    recv$: Observable<any>;
  } {
    const subject = webSocket<any>({
      url,
      serializer: JSON.stringify,
      deserializer: (e) => {
        try {
          return e.data && e.data !== 'undefined' 
            ? JSON.parse(e.data) 
            : null;
        } catch {
          return null;
        }
      },
      openObserver:  { next: () => this.debugService.info(url, '[WS] OPEN') },
      closeObserver: { next: () => this.debugService.info(url, '[WS] CLOSE') },
    });

    const recv$ = subject.pipe(
      retry({
        delay: (_, count) => {
          const backoff = Math.min(30_000, 1_000 * 2 ** count);
          return timer(backoff + Math.random() * 1_000);
        },
        resetOnSuccess: true,
      }),
      takeUntil(this.disconnect$),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    return { subject, recv$ };
  }

// ──────────────────────── Stream pipeline ────────────────────────
  private buildPipeline(): Observable<unknown> {
    return this.wsRecv$.pipe(
      filter((e) => e != null),
      tap((e) => {
        this.debugService.info(e, '[WS] chunk');
        if (!this.isTracked(e.completion_id))
          this.debugService.warn(e.completion_id, '[WS] untracked completion_id');
      }),
      filter((m) => this.isTracked(m.completion_id)),
      groupBy(
        (m) => m.completion_id,
        {
          duration: (grp) => merge(
            grp.pipe(filter((m) => m.event_type === 'final' || m.event_type === 'end'), take(1)),
            grp.pipe(filter((m) => m.event_type === 'error'), take(1)),
            this.cancel$.pipe(filter((id) => id === grp.key || id === this.baseId(grp.key)), take(1)),
            this.disconnect$.pipe(take(1)),
          ),
        },
      ),
      mergeMap((group$) => this.processGroup(group$)),
    );
  }

  private processGroup(group$: GroupedObservable<string, any>): Observable<never> {
    const id = group$.key;

    const textChunk$ = group$.pipe(
      filter((m) => m.event_type === 'chunk' && !!m.content?.text_content),
      map((m) => ({
        text: m.content.text_content as string,
        hiddenCommand: m.content.hidden_command as string | undefined,
      })),
    );

    const final$ = group$.pipe(filter((m) => m.event_type === 'final' || m.event_type === 'end'), take(1));
    const error$ = group$.pipe(filter((m) => m.event_type === 'error'), take(1));
    const cancelForId$ = this.cancel$.pipe(filter((x) => x === id), take(1));
    const stop$ = merge(final$, error$, cancelForId$, this.disconnect$).pipe(take(1));

    const textFlow$ = textChunk$.pipe(
      takeUntil(stop$),
      tap((chunk: { text: string; hiddenCommand?: string }) => {
        if (this.isCancelled(id)) 
          return;

        // this.disableLoader();
        this.textAnimation.enqueueText(id, chunk.text, chunk.hiddenCommand);
      }),
      ignoreElements(),
    );

    const onError$ = error$.pipe(
      tap(() => this.ngZone.run(() => this.showErrorBubble())),
      ignoreElements(),
    );

    return merge(textFlow$, onError$);
  }

  private setupToolsPipeline(): any {
    return this.toolsRecv$.pipe(
      filter(e => e != null),
      tap((e) => {
        if (!this.isTracked(e.completion_id))
          this.debugService.warn(e.completion_id, "[WebSocketService] Not processing Assistant CompletionID");
      }),
      // filter(m => this.completionsIds.includes(m.completion_id)),
      concatMap((evt) =>
        this.processToolEvent(evt).pipe(
          rxTimeout({ each: 30000 }), // 30s
          catchError((err) => {
            this.debugService.error(err, "[WebSocketService] Action Tool Failed");
            this.showErrorBubble();
            return EMPTY; // continúa con el siguiente evento
          }),
          concatMap(() => of(null).pipe(delay(500))), // espera 500ms antes del siguiente
        ),
      ),
      catchError((err) => {
          this.debugService.error(err, '[WebSocketService] Tools pipeline crashed, restarting');
          return this.setupToolsPipeline();
      })
    );
  }

  private processToolEvent(evt: any): Observable<void> {
    const result = this.actionService.actionFromChatBot(evt) as unknown;
    return this.toObservable(result).pipe(
      tap((e) => {
        this.debugService.debug(evt, "[WebSocketService] Tool action done");
      }),
      mapTo(void 0),
    );
  }

  private toObservable<T>(value: T | Promise<T> | Observable<T>): Observable<T> {
    const maybeObs = value as any;
    if (maybeObs?.subscribe) 
      return maybeObs as Observable<T>;

    if (value instanceof Promise) 
      return from(value);

    return of(value as T);
  }

// ──────────────────────── Public API ────────────────────────
  public trackCompletionId(id: string, _type: 'text' | 'audio' = 'text'): void {
    this.pruneTrackedIds();
    this.completionIds.add(id);
    this.activeCompletionId = id;
  }

  public async cancelCompletion(): Promise<void> {
    this.disableLoader();
    await this.playService.stopNow();

    const id = this.activeCompletionId;
    if (!id || this.cancelled.has(id)) 
      return;

    this.cancelled.add(id);
    this.cancel$.next(id);
    try { this.textAnimation.cancel(id); } catch {}
  }

// ──────────────────────── Internals ────────────────────────
  private sendPayload(payload: { completion_id: string; [k: string]: any }): void {
    this.activeCompletionId = payload.completion_id;
    try {
      this.wsSend.next(payload);
      this.completionIds.add(payload.completion_id);
    } catch {
      this.showErrorBubble();
    }
  }

  private isCancelled(id: string): boolean {
    return this.cancelled.has(id) || this.cancelled.has(this.baseId(id));
  }

  private isTracked(id: string): boolean {
    return this.completionIds.has(id) || this.completionIds.has(this.baseId(id));
  }

  private baseId(id: string): string {
    return this.dataChat.allowProactiveBubbles() 
      ? id?.split('_')[0] ?? id
      : 'now-allowed';
  }

  private pruneTrackedIds(): void {
    if (this.completionIds.size > MAX_TRACKED_IDS) {
      const iter = this.completionIds.values();
      for (let i = 0, half = this.completionIds.size >> 1; i < half; i++) {
        const v = iter.next().value;
        if (v) this.completionIds.delete(v);
      }
    }

    if (this.cancelled.size > MAX_TRACKED_IDS) 
      this.cancelled.clear();
  }

  public disableLoader(): void {
    this.dataChat.isStreaming.set(false);
    this.dataChat.chatIsThinking.set(false);
  }

  public enableLoader(): void {
    this.dataChat.isStreaming.set(true);
    this.dataChat.chatIsThinking.set(true);
  }

  private showErrorBubble(): void {
    this.dataChat.showConnectionErrorMessage();
    this.disableLoader();
  }

}